import { CellValueChangedEvent, ProcessCellForExportParams } from 'ag-grid-community';
import { StatusPanelDef } from 'ag-grid-enterprise';
import { forEach } from 'lodash';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useAuthUser } from 'auth/hooks';
import { SERVER_FORMAT_DATE_LONG } from 'core/constants';
import { TableLayoutType } from 'core/enums';
import { AlternateVersionHelper } from 'core/helpers';
import { AgGridClientTableProvider } from 'core/states';
import { AgGridDataRow, GetManyResponse, IBreadcrumbsItem } from 'core/types';
import { formatDateForServer, goTo, isEmptyArray, pascalCase, stripHtmlTags } from 'core/utils';
import { oldBomPartListPath } from 'jobs/routes/routePaths';
import { useExternalOrderStatus } from 'orders/hooks';
import { PartDetailsTab } from 'parts/components/PartDetails';
import { useCostDecimals } from 'pricing/hooks';
import { projectDetailsPath } from 'projects/routes/routePaths';
import {
  aggFuncs,
  getDefaultColDef,
  getOpenLineRowClassRules,
  getRowClass,
  isRowSelectable,
  QuotePartColField,
  QuotePartColId,
  StatusBar,
  useTableInlineActions,
} from 'quotes/components/QuotePartList';
import { useReplaceSelectedSpaConfirmation } from 'quotes/components/QuotePartSelectSpa';
import { QuoteEntityType, QuoteVersionType } from 'quotes/enums';
import { useGetCustomerQuoteParts, useQuotePartActions } from 'quotes/hooks';
import { useCustomerQuotePartsContext } from 'quotes/states';
import {
  ICustomerQuote,
  ICustomerQuotePart,
  ICustomerQuotePartListRequestPayload,
  IQuoteExtraData,
  IQuotePartExtraData,
} from 'quotes/types';
import { getQuoteVersion, getSelectedSpaIdentity } from 'quotes/utils';

import { columnDef } from './columns';
import CustomerQuotePartListView from './CustomerQuotePartListView';
import { ExpiredCostingProvider } from './expiredCosting';
import { getQuoteAfterRefetch, withInactiveProjectLinkFlags } from './quoteStatus';
import { onRowsReorder } from './table';

interface IProps {
  initialQuote: ICustomerQuote;
  quoteEntityType?: QuoteEntityType;
  extraQuoteData?: IQuoteExtraData;
}

const CUSTOMER_QUOTE_PART_ENTITY_TYPE = 'Line';

// Never surfaced: the grid provider catches what the cell handler throws and reverts the cell.
const SELECTED_SPA_KEPT = 'selectedSpaKept';

export const CustomerQuotePartList = ({
  initialQuote,
  quoteEntityType = QuoteEntityType.QUOTE,
  extraQuoteData = {
    pendingCliJobs: {},
    expiredCostingLinesCount: 0,
  },
}: IProps) => {
  const [quote, setQuote] = useState<ICustomerQuote>(initialQuote);
  const [defaultQuotePartDetailsTab, setDefaultQuotePartDetailsTab] = useState<
    PartDetailsTab | undefined
  >(undefined);
  const [isOpenResetPricingForm, setIsOpenResetPricingForm] = useState(false);
  const [resetPricingFormPayload, setResetPricingFormPayload] = useState(null);
  const [extraData, setExtraData] = useState<IQuotePartExtraData>(null);

  const { t } = useTranslation();

  const {
    quoteQuoteAssociation,
    setQuoteQuoteAssociation,
    setDraftVersionChanged,
    currentQuoteVersion,
    setCurrentQuoteVersion,
  } = useCustomerQuotePartsContext();

  const { reorderQuoteParts, upsertQuotePart } = useQuotePartActions();
  const { user } = useAuthUser();
  const costDecimals = useCostDecimals();

  const {
    activeInlineActionDrawer,
    openInlineActionDrawer,
    onCloseInlineActionDrawer,
    editLineId,
  } = useTableInlineActions({ setDefaultQuotePartDetailsTab });

  const rowClassRules = useMemo(() => getOpenLineRowClassRules(editLineId), [editLineId]);

  const { sseStatusData } = useExternalOrderStatus({ order: quote, enabled: !!quote?.isOrder });

  const payload: ICustomerQuotePartListRequestPayload = useMemo(
    () => ({
      filters: { 'quoteVersion.type': currentQuoteVersion },
      quoteId: quote?.id,
    }),
    [currentQuoteVersion, quote?.id]
  );

  const {
    records,
    isLoading: isDataLoading,
    loadingPercentage,
    currentPage,
  } = useGetCustomerQuoteParts(payload, {
    fetchAllPages: true,
    onSuccess: (_, pageData: GetManyResponse<ICustomerQuotePart>) =>
      setExtraData(pageData.extraData),
  });

  const tableId = useMemo(() => `customer${quoteEntityType}PartListTable`, [quoteEntityType]);

  const layoutType = useMemo(
    () =>
      QuoteEntityType.QUOTE === quoteEntityType
        ? TableLayoutType.CUSTOMER_QUOTE_PART
        : TableLayoutType.CUSTOMER_ORDER_PART,
    [quoteEntityType]
  );

  const hasRowReordering = useMemo(
    () => QuoteVersionType.DRAFT === currentQuoteVersion,
    [currentQuoteVersion]
  );

  useEffect(() => {
    // A refetch fired by a stopped submit can race the SSE event that already resolved the order.
    setQuote((prev) => getQuoteAfterRefetch({ current: prev, refetched: initialQuote }));
  }, [initialQuote]);

  useEffect(() => {
    if (!sseStatusData) {
      return;
    }

    setQuote((prev) => ({
      ...prev,
      status: sseStatusData.order.status ?? prev.status,
      externalStatus: sseStatusData.order.externalStatus ?? prev.externalStatus,
      externalOrderNumber: sseStatusData.order.externalOrderNumber ?? prev.externalOrderNumber,
      externalOrderUrl: sseStatusData.order.externalOrderUrl ?? prev.externalOrderUrl,
      externalOrderErrorMessage:
        sseStatusData.order.externalOrderErrorMessage ?? prev.externalOrderErrorMessage,
    }));
  }, [sseStatusData]);

  useEffect(() => {
    setCurrentQuoteVersion(getQuoteVersion(quote));
  }, [quote, setCurrentQuoteVersion]);

  useEffect(() => {
    if (!quoteQuoteAssociation) {
      return;
    }

    setIsOpenResetPricingForm(true);

    setResetPricingFormPayload({
      filtered: 1,
      quoteId: quoteQuoteAssociation.quoteId,
      returnQuoteParts: 1,
      filters: {
        vendorQuotes: quoteQuoteAssociation.vendorQuotes,
        deleted: 0,
        'quoteVersion.type': QuoteVersionType.DRAFT,
      },
    });
  }, [quoteQuoteAssociation]);

  const isDataLoadCompleted = useMemo(() => loadingPercentage === 100, [loadingPercentage]);

  const isAlternateVersionEnabled = useMemo(
    () => AlternateVersionHelper.isAlternateVersionEnabled({ quote }),
    [quote]
  );

  const quoteWithProjectLinkFlags = useMemo(
    () => withInactiveProjectLinkFlags(quote, initialQuote),
    [quote, initialQuote]
  );

  const statusBar = useMemo<{
    statusPanels: StatusPanelDef[];
  }>(
    () => ({
      statusPanels: [
        {
          statusPanel: StatusBar,
          statusPanelParams: {
            entityType: CUSTOMER_QUOTE_PART_ENTITY_TYPE,
            quoteId: quote?.id,
            quoteVersionType: currentQuoteVersion,
            isDataLoadCompleted: loadingPercentage === 100,
            companySettings: user?.companySettings,
            freightEnabled: quote?.freightEnabled,
            quoteEntityType,
            onFreightChanged: setDraftVersionChanged,
            quote: quoteWithProjectLinkFlags,
            onUpdateQuote: setQuote,
          },
          key: 'customerQuoteStatusBar',
          align: 'left',
        },
      ],
    }),
    [
      currentQuoteVersion,
      loadingPercentage,
      quote,
      quoteWithProjectLinkFlags,
      quoteEntityType,
      user?.companySettings,
      setDraftVersionChanged,
    ]
  );

  const columnDefs = useMemo(
    () =>
      columnDef({
        isDataLoadCompleted,
        currentQuoteVersion,
        openInlineActionDrawer,
        setDefaultQuotePartDetailsTab,
        minimumMarginThreshold: Number(user?.companySettings?.minimumMarginThreshold),
        hasTaxonomyColumn:
          isAlternateVersionEnabled && Boolean(user?.companySettings?.taxonomyEnabled),
        supplierEnabled: Boolean(user?.companySettings?.supplierEnabled),
        quoteEntityType,
        setDraftVersionChanged,
        hasRowReordering,
        isAlternateVersionEnabled,
        costDecimals,
      }),
    [
      isDataLoadCompleted,
      currentQuoteVersion,
      openInlineActionDrawer,
      user?.companySettings?.minimumMarginThreshold,
      user?.companySettings?.taxonomyEnabled,
      user?.companySettings?.supplierEnabled,
      costDecimals,
      isAlternateVersionEnabled,
      quoteEntityType,
      setDraftVersionChanged,
      hasRowReordering,
    ]
  );

  const defaultColDef = useMemo(() => getDefaultColDef(), []);

  const upsertFieldsMap = useMemo(
    () => ({
      computedPurchaseCost: 'purchaseCost',
      shipmentType: 'shipmentTypeId',
      corporateLocation: 'corporateLocationId',
    }),
    []
  );

  const upsertValueMap = useMemo(
    () => ({
      shipmentType: 'id',
      corporateLocation: 'id',
    }),
    []
  );

  const resetPricingFields = useMemo(() => [QuotePartColField.UOM, QuotePartColField.QUANTITY], []);

  const { confirmReplaceSelectedSpa, replaceSelectedSpaConfirmation } =
    useReplaceSelectedSpaConfirmation();

  const onCloseResetPricingForm = () => {
    setIsOpenResetPricingForm(false);

    if (!isEmptyArray(quoteQuoteAssociation?.vendorQuotes || [])) {
      setQuoteQuoteAssociation(null);
    }
  };

  const excelExportParams = useMemo(
    () => ({
      processCellCallback: (params: ProcessCellForExportParams) => {
        const colId = params.column.getColId();
        if (QuotePartColId.NOTES === colId) {
          const notes = [];

          forEach(params.value, (note) => {
            notes.push(`${t(`labelNoteType${pascalCase(note.type)}`)}:`);
            notes.push(`${stripHtmlTags(note.content).trim()}`);
          });

          return notes.join('\n');
        }

        return params.value;
      },
    }),
    [t]
  );

  const onCellValueChanged = useCallback(
    async (_: any, event: CellValueChangedEvent): Promise<{ updatedRow: AgGridDataRow }> => {
      const originalField = event.colDef.field;
      const field = upsertFieldsMap[originalField] || originalField;
      let newValue = event.newValue?.[upsertValueMap[originalField]] || event.newValue;

      if (originalField === 'pricingDate') {
        newValue = formatDateForServer(newValue, SERVER_FORMAT_DATE_LONG) || null;
      }

      // A typed cost drops the pick, as Overwrite does; throwing on a cancel reverts the cell.
      const selectedSpa = getSelectedSpaIdentity(event.data as ICustomerQuotePart);

      if (QuotePartColField.COMPUTED_PURCHASE_COST === originalField && selectedSpa) {
        const isConfirmed = await confirmReplaceSelectedSpa(selectedSpa.spaCostSource);

        if (!isConfirmed) {
          throw new Error(SELECTED_SPA_KEPT);
        }
      }

      const { success, error, quotePart } = await upsertQuotePart({
        quotePart: {
          id: event.data.id,
          partId: event.data.partId,
          [field]: newValue,
          ...(QuotePartColField.COMPUTED_PURCHASE_COST === originalField && {
            isCostingCustom: 1,
          }),
        },
      });

      if (!success) {
        throw new Error(error);
      }

      if (resetPricingFields.includes(field)) {
        setIsOpenResetPricingForm(true);
        setResetPricingFormPayload({
          quoteParts: [quotePart.id],
        });
      }

      setDraftVersionChanged();

      return { updatedRow: quotePart };
    },
    [
      confirmReplaceSelectedSpa,
      upsertFieldsMap,
      upsertValueMap,
      upsertQuotePart,
      resetPricingFields,
      setDraftVersionChanged,
    ]
  );

  const breadcrumbs: IBreadcrumbsItem[] = [
    {
      label: t('labelProject'),
      text: quote?.vendorProjectName,
      to: projectDetailsPath(quote?.vendorProjectId),
    },
    {
      label: t('labelJob'),
      text: quote?.jobName,
      onClick: () => goTo(oldBomPartListPath(quote?.vendorProjectId, quote?.bomId)),
    },
    {
      text: isAlternateVersionEnabled
        ? quote?.billToAddress?.name
        : quote?.customerContactGroup?.name,
    },
  ];

  return (
    <>
      <AgGridClientTableProvider
        tableId={tableId}
        entityType={CUSTOMER_QUOTE_PART_ENTITY_TYPE}
        columnDefs={columnDefs}
        data={records}
        isDataLoading={isDataLoading}
        loadingPercentage={loadingPercentage}
        dataCurrentPage={currentPage}
        statusBar={statusBar}
        defaultColDef={defaultColDef}
        aggFuncs={aggFuncs}
        getRowClass={useCallback(
          (params) => getRowClass({ ...params, quoteEntityType }),
          [quoteEntityType]
        )}
        rowClassRules={rowClassRules}
        onRowsReorder={onRowsReorder(reorderQuoteParts, setDraftVersionChanged) as any}
        onCellValueChanged={onCellValueChanged}
        hasRowReordering={hasRowReordering}
        layoutType={layoutType}
        hasDefaultStatusBar={false}
        hasLayoutActions
        headerCheckbox={isDataLoadCompleted}
        isRowSelectable={isRowSelectable}
        excelExportParams={excelExportParams}
      >
        <ExpiredCostingProvider
          quoteId={quote?.id}
          quoteVersionType={currentQuoteVersion}
          resetExpiredCostingCliJob={extraQuoteData?.pendingCliJobs?.resetExpiredCosting}
          expiredCostingAboveThreshold={extraQuoteData?.expiredCostingAboveThreshold}
          expiredCostingLinesCount={extraQuoteData?.expiredCostingLinesCount ?? 0}
        >
          <CustomerQuotePartListView
            quote={quoteWithProjectLinkFlags}
            setQuote={setQuote}
            pendingCliJobs={{
              ...extraQuoteData?.pendingCliJobs,
              fetchExternalInventory: extraData?.pendingCliJobs?.fetchExternalInventory ?? null,
            }}
            breadcrumbs={breadcrumbs}
            activeInlineActionDrawer={activeInlineActionDrawer}
            onCloseInlineActionDrawer={onCloseInlineActionDrawer}
            defaultQuotePartDetailsTab={defaultQuotePartDetailsTab}
            isOpenResetPricingForm={isOpenResetPricingForm}
            onCloseResetPricingForm={onCloseResetPricingForm}
            resetPricingFormPayload={resetPricingFormPayload}
            quoteEntityType={quoteEntityType}
            // From the details query, not the local quote: save/submit responses replace that
            // state without this field.
            projectSubmitBlock={initialQuote?.projectSubmitBlock}
            projectSubmitWarning={initialQuote?.projectSubmitWarning}
          />
        </ExpiredCostingProvider>
      </AgGridClientTableProvider>

      {replaceSelectedSpaConfirmation}
    </>
  );
};
